import { maxFileSize } from 'sidecar-utilities'

/**
 * Validates file sizes and filters out oversized files.
 *
 * @example
 *   const files = {
 *   "0": {
 *   lastModified: 1716486170125,
 *   lastModifiedDate: new Date("2024-05-23T17:42:50.125Z"),
 *   name: "Hamburger.jpg",
 *   size: 66218,
 *   type: "image/jpeg",
 *   webkitRelativePath: ""
 *   },
 *   "1": {
 *   ... another file object
 *   }
 *   };
 *   const handleOversizedFile = (file) => console.log(`${file.name} is too large`);
 *   const { validFiles, maxFileSize } = validateFileSize(files, handleOversizedFile);
 *
 * @param {Object} files - An object of files to validate, where keys are
 *   numerical strings and values are File objects.
 * @param {function(File): void} overSizeCallback - A callback function to be
 *   called for each oversized file.
 * @param {number} [maxSize=50e6] - The maximum allowed file size in bytes.
 *   Defaults to 50MB for jira related uploads. Default is `50e6`
 *
 * @typedef {Object} File
 * @property {number} lastModified - The timestamp of when the file was last
 *   modified.
 * @property {Date} lastModifiedDate - The date when the file was last modified.
 * @property {string} name - The name of the file.
 * @property {number} size - The size of the file in bytes.
 * @property {string} type - The MIME type of the file.
 * @property {string} webkitRelativePath - The relative path of the file
 *   (usually empty for most use cases).
 *
 * @typedef {Object} Files
 * @property {File} [0] - File object at index 0.
 * @property {File} [1] - File object at index 1.
 * @property {File} [2] - File object at index 2.
 * @property {File} [n] - File object at index n.
 *
 * @typedef {Object} ValidationResult
 * @property {File[]} validFiles - An array of files that passed the size
 *   validation.
 * @property {Object} maxFileSize - Metadata regarding the maxFileSize config
 *   used for validation.
 * @returns {ValidationResult} An object containing the valid files and the
 *   maxFileSize object.
 */
export const validateFileSize = (files, overSizeCallback, maxSize = 50e6) => {
  maxFileSize.size = maxSize

  // Convert files object to array
  const filesArray = Object.values(files)

  // callback function for each oversized file
  const oversizedFiles = filesArray.filter(
    file => !maxFileSize.isValidFileSize(file),
  )
  oversizedFiles.forEach(file => overSizeCallback(file, maxFileSize))

  // Filter valid files and keep as an array
  const validFiles = filesArray.filter(file =>
    maxFileSize.isValidFileSize(file),
  )

  return { validFiles, maxFileSize }
}

// Used when we have nothing better to name a download after
const FALLBACK_FILENAME = 'attachment'

// Only the types members actually upload to a request. Anything missing just
// means the download keeps its extensionless fallback name.
const MIME_EXTENSIONS = {
  'application/msword': 'doc',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'image/bmp': 'bmp',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/tiff': 'tiff',
  'image/webp': 'webp',
  'text/plain': 'txt',
}

// Strips the charset off `image/png;charset=UTF-8`
const normalizeMimeType = (mimeType = '') =>
  `${mimeType}`.split(';')[0].trim().toLowerCase()

const unquote = (value = '') => value.trim().replace(/^"|"$/g, '')

/**
 * Read the filename out of a Content-Disposition header
 *
 * The RFC 5987 `filename*` form is preferred when present, since it keeps
 * characters a plain `filename` has to strip.
 *
 * @param {string} [header] - The raw Content-Disposition header value
 * @returns {string} The filename, or an empty string if the header doesn't
 *   carry one
 */
export const parseContentDispositionFilename = (header = '') => {
  if (!header) return ''

  const encoded = /filename\*=\s*(?:utf-8'')?([^;]+)/i.exec(header)
  if (encoded) {
    const value = unquote(encoded[1])
    try {
      return decodeURIComponent(value)
    } catch {
      // A malformed escape sequence is no reason to lose the whole name
      return value
    }
  }

  const plain = /filename=\s*("[^"]*"|[^;]+)/i.exec(header)
  return plain ? unquote(plain[1]) : ''
}

/**
 * Work out what to name a downloaded attachment.
 *
 * Jira returns a null filename for some attachments. Handing that to an
 * anchor's download attribute saves the file as `null`, with no extension, so
 * the member downloads something their OS refuses to open. Falling back to the
 * attachment id plus an extension from the response's own mime type keeps the
 * file openable.
 *
 * @param {object} attachment
 * @param {string} [attachment.contentDisposition] - Content-Disposition header
 *   from the download response, which is authoritative when present
 * @param {string} [attachment.filename] - Filename we already know about, from
 *   the thumbnail endpoint or the comment body
 * @param {string} [attachment.attachmentId] - The Jira attachment id
 * @param {string} [attachment.mimeType] - Mime type of the downloaded blob.
 *   Note this is the download's own type, not the thumbnail's: Jira previews a
 *   PDF as a JPEG, so the thumbnail type would name the file wrongly.
 * @returns {string} A filename safe to download as
 */
export const resolveDownloadFilename = ({
  contentDisposition,
  filename,
  attachmentId,
  mimeType,
} = {}) => {
  const fromHeader = parseContentDispositionFilename(contentDisposition)
  if (fromHeader) return fromHeader
  if (filename) return filename

  const name = attachmentId
    ? `${FALLBACK_FILENAME}-${attachmentId}`
    : FALLBACK_FILENAME
  const extension = MIME_EXTENSIONS[normalizeMimeType(mimeType)]

  return extension ? `${name}.${extension}` : name
}
